import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axiosApi from "../server/service";
import toast from "react-hot-toast";
import { Save, Loader2, X, ChevronDown } from "lucide-react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "destructive" | "outline" | "primary";
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", ...props }, ref) => {
    const variants = {
      default: "bg-slate-900 text-white hover:bg-slate-700",
      destructive: "bg-red-600 text-white hover:bg-red-700",
      outline: "border border-slate-300 bg-white hover:bg-slate-100",
      primary: "bg-green-600 text-white hover:bg-green-700",
    };
    return (
      <button
        className={`flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ${variants[variant]} ${className}`}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

interface DropdownOption {
  value: string;
  label: string;
}

// interface FormattedDetails {
//   designInfo: { colorSpecification: string; id: string; designNo: string; clientName: string; category: string };
//   costing: { finalPrice: number; [key: string]: any };
//   materialComponents: { name: string; total: number; vendor: string | null }[];
//   rateComponents: { name: string; total: number; vendor: string | null }[];
//   images: { primaryImage?: { url: string; [key: string]: any } };
// }

interface FormattedDetails {
  designInfo: {
    colorSpecification: string;
    id: string;
    designNo: string;
    clientName: string;
    category: string;
  };
  costing: { finalPrice: number; [key: string]: any };

  materialComponents: {
    name: string;
    chartNo: string;
    total: number;
    vendor: string | null;
    key: string;
  }[];
  rateComponents: { name: string; total: number; vendor: string | null }[];
  images: { primaryImage?: { url: string; [key: string]: any } };
}

interface SelectedSegment {
  segmentName: string;
  quantity: string;
  unitPrice: number;
  pieces: number;
}

interface SizeSegmentData {
  _id: string;
  segmentName: string;
  numberOfSets: number;
}

const CreateOrderPage: React.FC = () => {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const isEditMode = Boolean(orderId);

  const [lotNumber, setLotNumber] = useState<string>("");
  const [designId, setDesignId] = useState<string>("");
  const [designs, setDesigns] = useState<DropdownOption[]>([]);
  // const [availableSizes, setAvailableSizes] = useState<DropdownOption[]>([]);
  const [availableSizes, setAvailableSizes] = useState<SizeSegmentData[]>([]);
  const [loadingStates, setLoadingStates] = useState({
    page: true,
    details: false,
    submit: false,
  });

  const [selectedSegments, setSelectedSegments] = useState<
    Map<string, SelectedSegment>
  >(new Map());
  const [segmentDetailsCache, setSegmentDetailsCache] = useState<
    Map<string, FormattedDetails>
  >(new Map());
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [isSizeDropdownOpen, setIsSizeDropdownOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsSizeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoadingStates((prev) => ({ ...prev, page: true }));
      try {
        if (isEditMode && orderId) {
          const [orderRes, designsRes, sizesRes] = await Promise.all([
            axiosApi.get(`/api/orders/getOrderById/${orderId}`),
            axiosApi.get("/api/designs/get-designs"),
            axiosApi.get("/api/sizeSegment/get-all-segments"),
          ]);

          const orderData = orderRes.data.data;
          // setDesigns(designsRes.data.data.map((d: any) => ({ value: d._id, label: d.basicInfo.designNo })));
          setDesigns(
            designsRes.data.data.map((d: any) => ({
              value: d.id,
              label: `${d.designNo} - ${d.colorSpecification}`,
            }))
          );
          // setAvailableSizes(sizesRes.data.map((s: any) => ({ value: s._id, label: s.segmentName })));
          setAvailableSizes(sizesRes.data);

          // Pre-fill form state from fetched order data
          setLotNumber(orderData.lotNumber);
          setDesignId(orderData.designId._id);

          // Convert orderDetails array to the Map structure used by the UI
          const segmentsMap = new Map<string, SelectedSegment>();
          orderData.orderDetails.forEach((detail: any) => {
            const fullSize = sizesRes.data.find(
              (s: any) => s._id === detail.sizeSegmentId._id
            );
            segmentsMap.set(detail.sizeSegmentId._id, {
              segmentName: detail.sizeSegmentId.segmentName,
              quantity: String(detail.quantity),
              unitPrice: detail.unitPrice,
              pieces: fullSize?.numberOfSets || 1,
            });
          });
          setSelectedSegments(segmentsMap);

          // Set the first selected segment as the active tab
          if (orderData.orderDetails.length > 0) {
            setActiveTab(orderData.orderDetails[0].sizeSegmentId._id);
          }
        } else {
          // For new orders, only fetch designs - size segments will be fetched when design is selected
          const designsRes = await axiosApi.get("/api/designs/get-designs");
          setDesigns(
            designsRes.data.data.map((d: any) => ({
              value: d.id,
              label: `${d.designNo} - ${d.colorSpecification}`,
            }))
          );
          // Don't fetch size segments here - wait for design selection
        }
      } catch (error) {
        toast.error("Failed to load initial page data.");
        console.error("Data loading error:", error);
      } finally {
        setLoadingStates((prev) => ({ ...prev, page: false }));
      }
    };

    loadInitialData();
  }, [orderId, isEditMode]);

  // Fetch size segments and generate lot number when design is selected (only for new orders)
  useEffect(() => {
    const handleDesignSelection = async () => {
      if (!isEditMode) {
        if (designId) {
          try {
            // Fetch size segments with design usage and generate lot number in parallel
            const [segmentsRes, lotRes] = await Promise.all([
              axiosApi.get<{
                message: string;
                data: Array<{
                  _id: string;
                  segmentName: string;
                  status: string;
                  numberOfSets: number;
                  isBeingUsedForThisDesign: boolean;
                }>;
              }>(`/api/sizeSegment/get-segments-with-design-usage/${designId}`),
              axiosApi.get(
                `/api/orders/generate-lot-number?designId=${designId}`
              ),
            ]);

            // Filter to only show segments where isBeingUsedForThisDesign is true
            const segmentsToShow = segmentsRes.data.data.filter(
              (s) =>
                s.status === "Active" && s.isBeingUsedForThisDesign === true
            );

            // Map to the expected format
            const mappedSizes = segmentsToShow.map((s) => ({
              _id: s._id,
              segmentName: s.segmentName,
              numberOfSets: s.numberOfSets,
              status: s.status,
            }));

            setAvailableSizes(mappedSizes);
            setLotNumber(lotRes.data.data.lotNumber);

            // Clear selected segments when design changes
            setSelectedSegments(new Map());
            setSegmentDetailsCache(new Map());
            setActiveTab(null);
          } catch (error) {
            toast.error("Failed to load design data.");
            console.error("Design selection error:", error);
          }
        } else {
          // Clear lot number and sizes if design is deselected
          setLotNumber("");
          setAvailableSizes([]);
          setSelectedSegments(new Map());
          setSegmentDetailsCache(new Map());
          setActiveTab(null);
        }
      }
    };

    handleDesignSelection();
  }, [designId, isEditMode]);

  useEffect(() => {
    const getDesignDetailsForTab = async () => {
      if (!designId || !activeTab || segmentDetailsCache.has(activeTab)) return;
      setLoadingStates((prev) => ({ ...prev, details: true }));
      try {
        const { data } = await axiosApi.get(
          `/api/designs/get-design-details/${designId}?sizeSegmentId=${activeTab}`
        );
        setSegmentDetailsCache((prevCache) =>
          new Map(prevCache).set(activeTab, data.data)
        );
        setSelectedSegments((prevMap) => {
          const newMap = new Map(prevMap);
          const segment = newMap.get(activeTab);
          if (segment) segment.unitPrice = data.data.costing.finalPrice || 0;
          return newMap;
        });
        console.log(
          "Fetched design details for segment:",
          activeTab,
          data.data
        );
      } catch (error) {
        const sizeName = availableSizes.find(
          (s) => s._id === activeTab
        )?.segmentName;
        const errorMessage = sizeName
          ? `Please fill the details for size "${sizeName}" in the Design Master.`
          : `Could not load details for the selected size.`;

        toast.error(errorMessage);

        handleSegmentToggle(activeTab);

        // toast.error(`Could not load details for the selected size.`);
        // handleSegmentToggle(activeTab, availableSizes.find(s => s.value === activeTab)?.label || "");
      } finally {
        setLoadingStates((prev) => ({ ...prev, details: false }));
      }
    };
    getDesignDetailsForTab();
  }, [designId, activeTab, availableSizes, segmentDetailsCache]);

  const handleSegmentToggle = (segmentId: string) => {
    const newSelected = new Map(selectedSegments);
    const sizeData = availableSizes.find((s) => s._id === segmentId);
    if (!sizeData) return;

    if (newSelected.has(segmentId)) {
      newSelected.delete(segmentId);
      const newCache = new Map(segmentDetailsCache);
      newCache.delete(segmentId);
      setSegmentDetailsCache(newCache);
    } else {
      newSelected.set(segmentId, {
        segmentName: sizeData.segmentName,
        quantity: "1",
        unitPrice: 0,
        pieces: sizeData.numberOfSets,
      });
    }
    setSelectedSegments(newSelected);
    if (newSelected.has(segmentId)) {
      setActiveTab(segmentId);
    } else if (activeTab === segmentId) {
      setActiveTab(newSelected.keys().next().value || null);
    }
  };

  const handleQuantityChange = (segmentId: string, quantity: string) => {
    const newSelected = new Map(selectedSegments);
    const segment = newSelected.get(segmentId);
    if (segment) {
      segment.quantity = quantity;
      newSelected.set(segmentId, segment);
      setSelectedSegments(newSelected);
    }
  };

  const overallSummary = useMemo(() => {
    let totalQuantity = 0;
    let totalPrice = 0;
    selectedSegments.forEach((segment) => {
      const qty = parseInt(segment.quantity, 10) || 0;
      totalQuantity += qty;
      totalPrice += qty * segment.unitPrice;
    });
    return { totalQuantity, totalPrice };
  }, [selectedSegments]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!designId || selectedSegments.size === 0) {
      toast.error("Please select a design and at least one size segment.");
      return;
    }

    let hasInvalidQuantity = false;
    const segmentsPayload: any[] = [];
    selectedSegments.forEach((data, id) => {
      const quantity = parseInt(data.quantity, 10);
      if (isNaN(quantity) || quantity < 1) hasInvalidQuantity = true;
      segmentsPayload.push({
        sizeSegmentId: id,
        quantity: quantity,
        unitPrice: data.unitPrice,
      });
    });

    if (hasInvalidQuantity) {
      toast.error(
        "Please ensure all selected sizes have a quantity of at least 1."
      );
      return;
    }

    setLoadingStates((prev) => ({ ...prev, submit: true }));

    const orderPayload = {
      lotNumber,
      designId,
      segments: segmentsPayload,
    };

    try {
      if (isEditMode) {
        await axiosApi.patch(
          `/api/orders/updateOrder/${orderId}`,
          orderPayload
        );
        toast.success("Order Updated Successfully!");
      } else {
        await axiosApi.post("/api/orders/create-order", orderPayload);
        toast.success("Multi-size Order Created Successfully!");
      }
      navigate("/ordermanagement");
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to save order.";
      toast.error(errorMessage);
    } finally {
      setLoadingStates((prev) => ({ ...prev, submit: false }));
    }
  };

  if (loadingStates.page)
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );

  const currentDetails = activeTab ? segmentDetailsCache.get(activeTab) : null;
  const currentQuantity = activeTab
    ? parseInt(selectedSegments.get(activeTab)?.quantity || "1", 10) || 1
    : 1;

  console.log("currentDetails.materialComponents", currentDetails);

  return (
    <div className="bg-slate-50 min-h-screen font-sans p-4 md:p-6">
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
              {isEditMode ? "Edit Order" : "Create New Order"}
            </h1>
            <p className="text-slate-600 mt-1">
              Configure order details and specifications
            </p>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <Link to="/ordermanagement" className="w-full">
              <Button variant="outline" type="button" className="w-full">
                Cancel
              </Button>
            </Link>
            <Button
              variant="primary"
              type="submit"
              disabled={loadingStates.submit || selectedSegments.size === 0}
              className="w-full"
            >
              {loadingStates.submit ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span className="ml-2">
                {loadingStates.submit
                  ? "Saving..."
                  : isEditMode
                  ? "Update Order"
                  : "Create Order"}
              </span>
            </Button>
          </div>
        </div>
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="w-full lg:max-w-md bg-white p-6 rounded-lg shadow-md border border-gray-300 space-y-5">
            <h2 className="text-xl font-semibold text-slate-800 border-b pb-3">
              Order Information
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Design Number
              </label>
              <select
                value={designId}
                onChange={(e) => setDesignId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                disabled={isEditMode}
              >
                <option value="">Select Design</option>
                {designs.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lot Number
              </label>
              <input
                type="text"
                value={lotNumber}
                onChange={(e) => setLotNumber(e.target.value)}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-l text-slate-600"
              />
            </div>
            <div ref={dropdownRef} className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Size Segment
              </label>
              <button
                type="button"
                disabled={!designId}
                onClick={() => setIsSizeDropdownOpen(!isSizeDropdownOpen)}
                className="w-full flex justify-between items-center px-3 py-2 border border-gray-300 rounded-lg bg-white text-left disabled:bg-slate-100"
              >
                <span>
                  {selectedSegments.size > 0
                    ? `${selectedSegments.size} size(s) selected`
                    : "Select sizes"}
                </span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    isSizeDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {isSizeDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {availableSizes.map((size) => (
                    <label
                      key={size._id}
                      className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSegments.has(size._id)}
                        onChange={() => handleSegmentToggle(size._id)}
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-800">
                        {size.segmentName}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {selectedSegments.size > 0 && (
              <div className="pt-2 border-t">
                <h3 className="text-md font-semibold text-gray-800 mb-3">
                  Enter Quantities
                </h3>

                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-200">
                      <tr>
                        <th className="py-2 px-3 text-left font-semibold text-green-900">
                          Size Segment
                        </th>
                        <th className="py-2 px-3 text-left font-semibold text-green-900">
                          Sets
                        </th>
                        <th className="py-2 px-3 text-left font-semibold text-green-900">
                          Quantity
                        </th>
                        <th className="py-2 px-3 text-right font-semibold text-green-900">
                          Total Price
                        </th>
                        <th className="py-2 px-3 w-10">
                          {/* For remove button */}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {Array.from(selectedSegments.entries()).map(
                        ([id, data]) => {
                          const quantityNum = parseInt(data.quantity, 10) || 0;
                          const rowTotal = quantityNum * data.unitPrice;

                          return (
                            <tr
                              key={id}
                              className="border-b border-slate-200 last:border-none"
                            >
                              <td className="p-2 align-middle">
                                <label className="font-medium text-gray-800">
                                  {data.segmentName}
                                </label>
                              </td>
                              <td className="p-2 align-middle">
                                {data.pieces}
                              </td>

                              <td className="p-2 align-middle">
                                <input
                                  type="number"
                                  min="1"
                                  placeholder="Qty"
                                  value={data.quantity}
                                  onChange={(e) =>
                                    handleQuantityChange(id, e.target.value)
                                  }
                                  className="w-20 px-2 py-1.5 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 focus:border-green-500"
                                />
                              </td>
                              <td className="p-2 align-middle text-right font-medium text-gray-800">
                                {data.unitPrice > 0 && (
                                  <span>
                                    {rowTotal.toLocaleString("en-IN", {
                                      style: "currency",
                                      currency: "INR",
                                    })}
                                  </span>
                                )}
                              </td>
                              <td className="p-2 align-middle text-center">
                                <button
                                  type="button"
                                  onClick={() => handleSegmentToggle(id)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {selectedSegments.size > 0 && (
              <div className="space-y-2 pt-4 border-t animate-fade-in">
                <h3 className="text-md font-semibold text-gray-800">
                  Overall Order Summary
                </h3>
                <div className="flex justify-between items-center bg-blue-50 p-3 rounded-lg">
                  <span className="text-sm font-medium text-blue-800">
                    Total Quantity:
                  </span>
                  <span className="font-bold text-blue-900">
                    {overallSummary.totalQuantity.toLocaleString("en-IN")}
                  </span>
                </div>
                <div className="flex justify-between items-center bg-blue-100 p-3 rounded-lg">
                  <span className="font-semibold text-blue-900">
                    Total Price:
                  </span>
                  <span className="font-bold text-xl text-blue-900">
                    {overallSummary.totalPrice.toLocaleString("en-IN", {
                      style: "currency",
                      currency: "INR",
                    })}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="w-full flex-1 bg-white p-6 rounded-lg shadow-md border border-gray-300">
            {selectedSegments.size > 0 ? (
              <>
                <div className="flex items-center border-b border-gray-200 overflow-x-auto whitespace-nowrap">
                  {Array.from(selectedSegments.keys()).map((id) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setActiveTab(id)}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${
                        activeTab === id
                          ? "border-b-2 border-green-600 text-green-700"
                          : "text-slate-500 hover:text-slate-800"
                      }`}
                    >
                      {selectedSegments.get(id)?.segmentName}
                    </button>
                  ))}
                </div>
                <div className="mt-4">
                  {loadingStates.details &&
                  activeTab &&
                  !segmentDetailsCache.has(activeTab) ? (
                    <div className="flex justify-center items-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
                    </div>
                  ) : !currentDetails ? (
                    <div className="text-center text-slate-500 py-8">
                      <p>Details for this segment could not be loaded.</p>
                    </div>
                  ) : (
                    <div className="animate-fade-in">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-20 h-20 bg-slate-100 rounded-md flex items-center justify-center text-slate-400 flex-shrink-0">
                          {currentDetails.images?.primaryImage?.url ? (
                            <img
                              src={currentDetails.images.primaryImage.url}
                              alt={currentDetails.designInfo.designNo}
                              className="w-full h-full object-cover rounded-md"
                            />
                          ) : (
                            <span>No Image</span>
                          )}
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-slate-800">
                            {currentDetails.designInfo.designNo}
                          </h3>
                          <p className="text-slate-600">
                            {currentDetails.designInfo.category}
                          </p>
                          <div className="mt-2 bg-blue-50 text-blue-900 font-bold px-3 py-1 rounded-md inline-block">
                            Unit Price:{" "}
                            {currentDetails.costing.finalPrice.toLocaleString(
                              "en-IN",
                              { style: "currency", currency: "INR" }
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 space-y-6">
                        {currentDetails.materialComponents &&
                          currentDetails.materialComponents.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-gray-700 mb-2">
                                Material Usage
                              </h4>
                              <div className="rounded-lg border border-slate-200 overflow-hidden">
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="p-3 text-left font-semibold text-slate-800">
                                        Component
                                      </th>
                                      <th className="p-3 text-left font-semibold text-slate-800">
                                        Vendor
                                      </th>
                                      <th className="p-3 text-left font-semibold text-slate-800">
                                        Chart No.
                                      </th>
                                      <th className="py-3 px-4 text-right font-semibold text-slate-800">
                                        Meters
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white">
                                    {currentDetails.materialComponents.map(
                                      (comp, index) => (
                                        <tr
                                          key={`mat-${index}`}
                                          className="border-b border-slate-200 last:border-none"
                                        >
                                          <td className="p-3 font-medium text-slate-800">
                                            {comp.name}
                                          </td>
                                          <td className="p-3 text-slate-500 italic">
                                            {comp.vendor || "N/A"}
                                          </td>
                                          <td className="p-3 text-slate-700">
                                            {comp.chartNo || ""}
                                          </td>
                                          <td className="py-3 px-4 text-right text-slate-700 font-semibold">
                                            {(
                                              comp.total * currentQuantity
                                            ).toLocaleString("en-IN")}{" "}
                                            m
                                          </td>
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                        {/* {currentDetails.materialComponents && currentDetails.materialComponents.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-2">
                            Material Usage (Total for {currentQuantity} Sets)
                          </h4>
                          <div className="rounded-lg border border-slate-200 overflow-hidden">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="p-3 text-left font-semibold text-slate-800 w-[15%]">
                                    Component
                                  </th>
                                  <th className="p-3 text-left font-semibold text-slate-800 w-[30%]">
                                    Type/Name
                                  </th>
                                  <th className="p-3 text-left font-semibold text-slate-800 w-[15%]">
                                    Chart No.
                                  </th>
                                  <th className="p-3 text-right font-semibold text-slate-800 w-[20%]">
                                    Total Meters
                                  </th>
                                  <th className="p-3 text-left font-semibold text-slate-800 w-[20%]">
                                    Vendor
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white">
                                {currentDetails.materialComponents.map((comp, index) => (
                                  <tr
                                    key={`mat-${index}`}
                                    className="border-b border-slate-200 last:border-none"
                                  >
                                    
                                    <td className="p-3 font-medium text-slate-800">
                                      {comp.key}
                                    </td>
                                   
                                    <td className="p-3 text-slate-700 font-semibold">
                                      {comp.name}
                                    </td>
                                 
                                    <td className="p-3 text-slate-700">
                                      {comp.chartNo || "N/A"}
                                    </td>
                                
                                    <td className="p-3 text-right text-slate-700 font-semibold">
                                      {(comp.total * currentQuantity).toLocaleString("en-IN")} m
                                    </td>
                                  
                                    <td className="p-3 text-slate-500 italic">
                                      {comp.vendor || "N/A"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                    )} */}

                        {currentDetails.rateComponents &&
                          currentDetails.rateComponents.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-gray-700 mb-2">
                                Direct Rates & Charges
                              </h4>
                              <div className="rounded-lg border border-slate-200 overflow-hidden">
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="p-3 text-left font-semibold text-slate-800">
                                        Component
                                      </th>
                                      <th className="p-3 text-left font-semibold text-slate-800">
                                        Vendor
                                      </th>
                                      <th className="py-3 px-4 text-right font-semibold text-slate-800">
                                        Cost
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white">
                                    {currentDetails.rateComponents.map(
                                      (comp, index) => (
                                        <tr
                                          key={`rate-${index}`}
                                          className="border-b border-slate-200 last:border-none"
                                        >
                                          <td className="p-3 font-medium text-slate-800">
                                            {comp.name}
                                          </td>
                                          <td className="p-3 text-slate-500 italic">
                                            {comp.vendor || ""}
                                          </td>
                                          <td className="py-3 px-4 text-right text-slate-700 font-semibold">
                                            {comp.total.toLocaleString(
                                              "en-IN",
                                              {
                                                style: "currency",
                                                currency: "INR",
                                                minimumFractionDigits: 0,
                                                maximumFractionDigits: 2,
                                              }
                                            )}
                                          </td>
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center text-slate-500 h-full flex items-center justify-center">
                <p>
                  {!designId
                    ? "Select a design to begin."
                    : "Select one or more size segments to view details."}
                </p>
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateOrderPage;
